import {
  ApiRequest,
  ApiResponse,
  fetchDataFromAPITyped,
} from './api-requests';

/**
 * Revenue split configuration between host and partner for a specific charger
 */
export interface RevenueSplit {
  splitId: string;
  chargerId: string;
  hostUserId: string;
  partnerUserId: string;
  partnerUsername?: string;
  partnerEmail?: string;
  splitPercentage: number;
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'REVOKED';
  startDate: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  partner?: {
    uuid: string;
    username: string;
    email?: string;
    payout_account_id?: string;
    hostPayoutStatus?: string;
    hasPayoutAccount?: boolean;
    payoutStatus?: 'ENABLED' | 'DISABLED';
  };
}

/**
 * Response from creating a revenue split
 */
export interface CreateRevenueSplitResponse {
  message: string;
  revenueSplit: RevenueSplit;
}

/**
 * Response from getting charger splits
 */
export interface GetChargerSplitsResponse {
  chargerSplits: RevenueSplit[];
  totalActivePercentage: number;
  remainingPercentage: number;
}

/**
 * Create a revenue split for a partner on a charger
 * 
 * @param chargerId - UUID of the charger
 * @param partnerUsername - Username or email of the partner
 * @param splitPercentage - Percentage of revenue to split (0.01-100.00)
 * @returns Promise<ApiResponse<CreateRevenueSplitResponse | null>>
 * 
 * @example
 * ```typescript
 * const response = await createRevenueSplit(
 *   'charger-uuid', 
 *   'partner@example.com', 
 *   25.00
 * );
 * if (isSuccessful(response.status)) {
 *   console.log('Split created:', response.payload?.revenueSplit);
 * }
 * ```
 */
export const createRevenueSplit = async (
  chargerId: string,
  partnerUsername: string,
  splitPercentage: number
): Promise<ApiResponse<CreateRevenueSplitResponse | null>> => {
  const request: ApiRequest = {
    route: `revenue-splits/${chargerId}/splits`,
    request: {
      method: 'POST',
      entity: 'revenue-splits',
      body: {
        partnerUsername,
        splitPercentage,
      },
    },
  };

  return fetchDataFromAPITyped<CreateRevenueSplitResponse>(request);
};

/**
 * Get all revenue splits for a charger
 * 
 * @param chargerId - UUID of the charger
 * @param status - Optional status filter ('ACTIVE', 'INACTIVE', 'PENDING', 'REVOKED')
 * @returns Promise<ApiResponse<GetChargerSplitsResponse | null>>
 * 
 * @example
 * ```typescript
 * const response = await getChargerRevenueSplits('charger-uuid', 'ACTIVE');
 * if (isSuccessful(response.status)) {
 *   console.log('Active splits:', response.payload?.chargerSplits);
 *   console.log('Total percentage:', response.payload?.totalActivePercentage);
 * }
 * ```
 */
export const getChargerRevenueSplits = async (
  chargerId: string,
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'REVOKED'
): Promise<ApiResponse<GetChargerSplitsResponse | null>> => {
  const searchParams = new URLSearchParams();
  if (status) {
    searchParams.append('status', status);
  }

  const request: ApiRequest = {
    route: `revenue-splits/${chargerId}/splits`,
    request: {
      method: 'GET',
      entity: 'revenue-splits',
      body: undefined,
      searchParams: status ? searchParams : undefined,
    },
  };

  return fetchDataFromAPITyped<GetChargerSplitsResponse>(request);
};

/**
 * Update a revenue split's percentage or status
 * 
 * @param chargerId - UUID of the charger
 * @param splitId - UUID of the revenue split
 * @param updates - Object containing splitPercentage and/or status to update
 * @returns Promise<ApiResponse<{ message: string; revenueSplit: RevenueSplit } | null>>
 * 
 * @example
 * ```typescript
 * const response = await updateRevenueSplit(
 *   'charger-uuid',
 *   'split-uuid',
 *   { splitPercentage: 30.00 }
 * );
 * if (isSuccessful(response.status)) {
 *   console.log('Split updated:', response.payload?.revenueSplit);
 * }
 * ```
 */
export const updateRevenueSplit = async (
  chargerId: string,
  splitId: string,
  updates: {
    splitPercentage?: number;
    status?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'REVOKED';
  }
): Promise<ApiResponse<{ message: string; revenueSplit: RevenueSplit } | null>> => {
  const request: ApiRequest = {
    route: `revenue-splits/${chargerId}/splits/${splitId}`,
    request: {
      method: 'PUT',
      entity: 'revenue-splits',
      body: updates,
    },
  };

  return fetchDataFromAPITyped<{ message: string; revenueSplit: RevenueSplit }>(
    request
  );
};

/**
 * Revoke a revenue split (soft delete)
 * Sets status to REVOKED and endDate to now
 * 
 * @param chargerId - UUID of the charger
 * @param splitId - UUID of the revenue split
 * @returns Promise<ApiResponse<{ message: string } | null>>
 * 
 * @example
 * ```typescript
 * const response = await revokeRevenueSplit('charger-uuid', 'split-uuid');
 * if (isSuccessful(response.status)) {
 *   console.log('Split revoked:', response.payload?.message);
 * }
 * ```
 */
export const revokeRevenueSplit = async (
  chargerId: string,
  splitId: string
): Promise<ApiResponse<{ message: string } | null>> => {
  const request: ApiRequest = {
    route: `revenue-splits/${chargerId}/splits/${splitId}`,
    request: {
      method: 'DELETE',
      entity: 'revenue-splits',
      body: undefined,
    },
  };

  return fetchDataFromAPITyped<{ message: string }>(request);
};

/**
 * Copy revenue splits from one charger to another
 * 
 * @param targetChargerId - UUID of the charger to copy splits TO
 * @param sourceChargerId - UUID of the charger to copy splits FROM
 * @returns Promise<ApiResponse<CopySplitsResponse | null>>
 * 
 * @example
 * ```typescript
 * const response = await copySplitsFromCharger('target-uuid', 'source-uuid');
 * if (isSuccessful(response.status)) {
 *   console.log(`Copied ${response.payload?.copiedCount} splits`);
 * }
 * ```
 */
export interface CopySplitsResponse {
  message: string;
  copiedCount: number;
  splits: Array<{
    splitId: string;
    partnerUsername: string;
    splitPercentage: number;
  }>;
}

export const copySplitsFromCharger = async (
  targetChargerId: string,
  sourceChargerId: string
): Promise<ApiResponse<CopySplitsResponse | null>> => {
  const request: ApiRequest = {
    route: `revenue-splits/${targetChargerId}/splits/copy`,
    request: {
      method: 'POST',
      entity: 'revenue-splits',
      body: {
        sourceChargerId,
      },
    },
  };

  return fetchDataFromAPITyped<CopySplitsResponse>(request);
};

/**
 * Revenue split analytics for hosts
 */
export interface RevenueSplitAnalytics {
  summary: {
    totalGrossRevenue: number;
    totalNetRevenue: number;
    totalSplitDeductions: number;
    chargerCount: number;
    activePartnerCount: number;
    inactivePartnerCount: number;
  };
  topPartners: Array<{
    partnerId: string;
    partnerUsername: string;
    partnerEmail: string;
    totalEarnings: number;
    chargerCount: number;
    payoutStatus: 'ENABLED' | 'DISABLED';
    hasPayoutAccount: boolean;
  }>;
  topChargers: Array<{
    chargerId: string;
    chargerName: string;
    grossRevenue: number;
    netRevenue: number;
    splitDeductions: number;
    splitCount: number;
  }>;
  monthlyTrend: Array<{
    month: string;
    grossRevenue: number;
    netRevenue: number;
    splitDeductions: number;
  }>;
  allPartners: Array<{
    partnerId: string;
    partnerUsername: string;
    partnerEmail: string;
    chargerCount: number;
    totalPaid: number;
    totalPending: number;
    totalWithheld: number;
    status: 'ACTIVE' | 'INACTIVE';
    payoutStatus: 'ENABLED' | 'DISABLED';
    hasPayoutAccount: boolean;
    chargers: Array<{
      chargerId: string;
      chargerName: string;
      splitPercentage: number;
    }>;
  }>;
  message: string;
}

/**
 * Get revenue split analytics for a host user
 * 
 * @param userId - UUID of the host user
 * @param startDate - Optional start date (ISO string)
 * @param endDate - Optional end date (ISO string)
 * @returns Promise<ApiResponse<RevenueSplitAnalytics | null>>
 */
export const getRevenueSplitAnalytics = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<ApiResponse<RevenueSplitAnalytics | null>> => {
  const searchParams = new URLSearchParams();
  if (startDate) searchParams.append('startDate', startDate);
  if (endDate) searchParams.append('endDate', endDate);

  const request: ApiRequest = {
    route: `revenue-splits/host/${userId}/analytics`,
    request: {
      method: 'GET',
      entity: 'revenue-splits',
      body: undefined,
      searchParams: searchParams.toString() ? searchParams : undefined,
    },
  };

  return fetchDataFromAPITyped<RevenueSplitAnalytics>(request);
};

/**
 * Partner split earnings dashboard data
 */
export interface PartnerSplitEarnings {
  partner: {
    userId: string;
    username: string;
    email: string;
    hasPayoutAccount: boolean;
    payoutStatus: 'ENABLED' | 'DISABLED';
  };
  summary: {
    totalEarnings: number;
    totalPending: number;
    totalPaid: number;
    totalWithheld: number;
    monthlyEarnings: number;
    chargerCount: number;
  };
  monthlyTrend: Array<{
    month: string;
    earnings: number;
    status: 'CALCULATED' | 'PENDING' | 'PAID' | 'WITHHELD';
  }>;
  byCharger: Array<{
    chargerId: string;
    chargerName: string;
    hostUsername: string;
    splitPercentage: number;
    monthlyEarnings: number;
    totalEarnings: number;
    status: 'ACTIVE' | 'INACTIVE';
  }>;
  recentPayouts: Array<{
    payoutId: string;
    amount: number;
    dateRangeFrom: string;
    dateRangeTo: string;
    status: 'PENDING' | 'PAID' | 'WITHHELD';
    paidOn?: string;
  }>;
  message: string;
}

/**
 * Get partner split earnings data
 * 
 * @param userId - UUID of the partner user
 * @param startDate - Optional start date (ISO string)
 * @param endDate - Optional end date (ISO string)
 * @returns Promise<ApiResponse<PartnerSplitEarnings | null>>
 */
export const getPartnerSplitEarnings = async (
  userId: string,
  startDate?: string,
  endDate?: string
): Promise<ApiResponse<PartnerSplitEarnings | null>> => {
  const searchParams = new URLSearchParams();
  if (startDate) searchParams.append('startDate', startDate);
  if (endDate) searchParams.append('endDate', endDate);

  const request: ApiRequest = {
    route: `revenue-splits/partner/${userId}/earnings`,
    request: {
      method: 'GET',
      entity: 'revenue-splits',
      body: undefined,
      searchParams: searchParams.toString() ? searchParams : undefined,
    },
  };

  return fetchDataFromAPITyped<PartnerSplitEarnings>(request);
};

/**
 * Charger revenue summary with split breakdown
 */
export interface ChargerRevenueSummary {
  charger: {
    chargerId: string;
    chargerName: string;
  };
  summary: {
    grossRevenue: number;
    splitDeductions: number;
    netRevenue: number;
    transactionCount: number;
    period: string;
  };
  activeSplits: Array<{
    splitId: string;
    partnerUsername: string;
    splitPercentage: number;
    totalDeducted: number;
    status: 'ACTIVE' | 'INACTIVE';
  }>;
  splitBreakdown: {
    hostPercentage: number;
    hostAmount: number;
    partners: Array<{
      partnerUsername: string;
      percentage: number;
      amount: number;
    }>;
  };
}

/**
 * Get revenue summary for a specific charger
 * 
 * @param chargerId - UUID of the charger
 * @param period - 'month' | 'quarter' | 'year' | 'all' (default: 'month')
 * @returns Promise<ApiResponse<ChargerRevenueSummary | null>>
 */
export const getChargerRevenueSummary = async (
  chargerId: string,
  period: 'month' | 'quarter' | 'year' | 'all' = 'month'
): Promise<ApiResponse<ChargerRevenueSummary | null>> => {
  const searchParams = new URLSearchParams();
  searchParams.append('period', period);

  const request: ApiRequest = {
    route: `revenue-splits/${chargerId}/revenue-summary`,
    request: {
      method: 'GET',
      entity: 'revenue-splits',
      body: undefined,
      searchParams,
    },
  };

  return fetchDataFromAPITyped<ChargerRevenueSummary>(request);
};

